import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Search, Users, Lock, Shield, ChevronDown, ChevronUp, X, Pencil, Phone, Mail, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLANS = ['starter', 'professional', 'enterprise'];
const STATUSES = ['active', 'cancelled', 'suspended'];

export default function SaasAdminOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editOrgModal, setEditOrgModal] = useState(null);

  useEffect(() => {
    fetchOrgs();
  }, []);

  const fetchOrgs = async () => {
    try {
      const token = localStorage.getItem('saas_admin_token');
      const response = await axios.get(`${API}/saas-admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgs(response.data);
    } catch {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgDetail = async (orgId) => {
    try {
      const token = localStorage.getItem('saas_admin_token');
      const response = await axios.get(`${API}/saas-admin/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgDetail(response.data);
    } catch {
      toast.error('Failed to load organization details');
    }
  };

  const handleToggleExpand = (orgId) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
      setOrgDetail(null);
    } else {
      setExpandedOrg(orgId);
      fetchOrgDetail(orgId);
    }
  };

  const handleUpdateSubscription = async (orgId, plan, status) => {
    try {
      const token = localStorage.getItem('saas_admin_token');
      await axios.put(`${API}/saas-admin/organizations/${orgId}/subscription`, { plan, status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscription updated');
      setEditModal(null);
      fetchOrgs();
      if (expandedOrg === orgId) fetchOrgDetail(orgId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleUpdateOrganization = async (orgId, data) => {
    try {
      const token = localStorage.getItem('saas_admin_token');
      await axios.put(`${API}/saas-admin/organizations/${orgId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Organization updated');
      setEditOrgModal(null);
      fetchOrgs();
      if (expandedOrg === orgId) fetchOrgDetail(orgId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization');
    }
  };

  const filtered = orgs.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">Loading organizations...</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-manrope font-bold text-3xl text-white tracking-tight mb-1">Organizations</h1>
        <p className="text-gray-400 text-sm">{orgs.length} organization{orgs.length !== 1 ? 's' : ''} registered</p>
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
        <input
          type="text"
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none text-sm"
          data-testid="admin-search-orgs"
        />
      </div>

      {/* Organizations List */}
      <div className="space-y-3">
        {filtered.map((org) => (
          <div key={org.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            {/* Org Row */}
            <div
              className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-800/50 transition-colors"
              onClick={() => handleToggleExpand(org.id)}
              data-testid={`org-row-${org.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 font-bold">
                  {org.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-white font-medium">{org.name}</p>
                  <p className="text-gray-500 text-xs">Created: {org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-1.5 text-gray-400">
                    <Users size={14} />
                    <span>{org.user_count}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-400">
                    <Lock size={14} />
                    <span>{org.credential_count}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-400">
                    <Shield size={14} />
                    <span>{org.certificate_count}</span>
                  </div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  org.subscription_plan === 'enterprise' ? 'bg-purple-500/10 text-purple-400' :
                  org.subscription_plan === 'professional' ? 'bg-amber-500/10 text-amber-400' :
                  'bg-gray-700 text-gray-400'
                }`}>
                  {(org.subscription_plan || 'starter').charAt(0).toUpperCase() + (org.subscription_plan || 'starter').slice(1)}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  org.subscription_status === 'active' ? 'bg-emerald-500/10 text-emerald-400' :
                  org.subscription_status === 'suspended' ? 'bg-red-500/10 text-red-400' :
                  org.subscription_status === 'cancelled' ? 'bg-gray-700 text-gray-400' :
                  'bg-emerald-500/10 text-emerald-400'
                }`}>
                  {(org.subscription_status || 'active').charAt(0).toUpperCase() + (org.subscription_status || 'active').slice(1)}
                </span>
                {expandedOrg === org.id ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
              </div>
            </div>

            {/* Expanded Detail */}
            {expandedOrg === org.id && orgDetail && (
              <div className="border-t border-gray-800 p-5 bg-gray-800/30">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold">Organization Details</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditOrgModal(orgDetail); }}
                      className="flex items-center gap-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      data-testid={`btn-edit-org-${org.id}`}
                    >
                      <Pencil size={14} />
                      Edit Details
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditModal(org); }}
                      className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      data-testid={`btn-edit-subscription-${org.id}`}
                    >
                      Manage Subscription
                    </button>
                  </div>
                </div>

                {/* Org Info + Primary Contact */}
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-3">
                    <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                      <p className="text-gray-500 text-xs mb-1">Plan</p>
                      <p className="text-white font-medium capitalize">{orgDetail.subscription_plan || 'starter'}</p>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                      <p className="text-gray-500 text-xs mb-1">Status</p>
                      <p className="text-white font-medium capitalize">{orgDetail.subscription_status || 'active'}</p>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                      <p className="text-gray-500 text-xs mb-1">Resources</p>
                      <p className="text-white text-sm">{orgDetail.credential_count} creds &middot; {orgDetail.certificate_count} certs</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                      <p className="text-gray-500 text-xs mb-2">Primary Contact</p>
                      {orgDetail.primary_contact_name ? (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-white text-sm">
                            <User size={14} className="text-gray-500" />
                            <span>{orgDetail.primary_contact_name}</span>
                          </div>
                          {orgDetail.primary_contact_email && (
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <Mail size={14} className="text-gray-500" />
                              <span>{orgDetail.primary_contact_email}</span>
                            </div>
                          )}
                          {orgDetail.primary_contact_phone && (
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <Phone size={14} className="text-gray-500" />
                              <span>{orgDetail.primary_contact_phone}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm italic">No primary contact set</p>
                      )}
                    </div>
                    {orgDetail.details && (
                      <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                        <p className="text-gray-500 text-xs mb-1">Company Details</p>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{orgDetail.details}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Users Table */}
                <h4 className="text-gray-300 font-medium text-sm mb-2">Users ({orgDetail.users?.length || 0})</h4>
                <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-gray-400 font-medium text-xs">Name</th>
                        <th className="px-4 py-2.5 text-left text-gray-400 font-medium text-xs">Email</th>
                        <th className="px-4 py-2.5 text-left text-gray-400 font-medium text-xs">Role</th>
                        <th className="px-4 py-2.5 text-left text-gray-400 font-medium text-xs">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {(orgDetail.users || []).map((u) => (
                        <tr key={u.id} className="hover:bg-gray-800/50">
                          <td className="px-4 py-2.5 text-white">{u.full_name}</td>
                          <td className="px-4 py-2.5 text-gray-400">{u.email}</td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300 uppercase">{u.role}</span>
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">No organizations found</p>
          </div>
        )}
      </div>

      {/* Edit Subscription Modal */}
      {editModal && (
        <SubscriptionModal
          org={editModal}
          onClose={() => setEditModal(null)}
          onSave={handleUpdateSubscription}
        />
      )}

      {/* Edit Organization Modal */}
      {editOrgModal && (
        <EditOrgModal
          org={editOrgModal}
          onClose={() => setEditOrgModal(null)}
          onSave={handleUpdateOrganization}
        />
      )}
    </div>
  );
}

function SubscriptionModal({ org, onClose, onSave }) {
  const [plan, setPlan] = useState(org.subscription_plan || 'starter');
  const [status, setStatus] = useState(org.subscription_status || 'active');
  const defaultLimits = { starter: { max_credentials: 10, max_certificates: 5, max_users: 3, file_attachments: false, export: false, audit_logs: false }, professional: { max_credentials: 100, max_certificates: 50, max_users: 20, file_attachments: true, export: true, audit_logs: false }, enterprise: { max_credentials: -1, max_certificates: -1, max_users: -1, file_attachments: true, export: true, audit_logs: true } };
  const initialLimits = org.plan_limits || defaultLimits[org.subscription_plan || 'starter'] || defaultLimits.starter;
  const [limits, setLimits] = useState(initialLimits);
  const [savingLimits, setSavingLimits] = useState(false);

  const handlePlanChange = (newPlan) => {
    setPlan(newPlan);
    setLimits(defaultLimits[newPlan] || defaultLimits.starter);
  };

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      const token = localStorage.getItem('saas_admin_token');
      await axios.put(`${API}/saas-admin/organizations/${org.id}/plan-limits`, limits, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan limits updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update limits');
    } finally {
      setSavingLimits(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="subscription-modal">
      <div className="bg-gray-900 w-full max-w-lg rounded-xl border border-gray-800 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
          <h2 className="font-manrope font-bold text-lg text-white">Manage Subscription</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-gray-400 text-sm mb-1">Organization</p>
            <p className="text-white font-medium">{org.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Plan</label>
            <select
              value={plan}
              onChange={(e) => handlePlanChange(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white focus:border-amber-500 focus:outline-none"
              data-testid="select-plan"
            >
              {PLANS.map((p) => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white focus:border-amber-500 focus:outline-none"
              data-testid="select-status"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Plan Limits */}
          <div className="border-t border-gray-800 pt-4">
            <p className="text-sm font-medium text-amber-400 mb-3">Plan Limits</p>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Max Credentials</label>
                  <input
                    type="number"
                    value={limits.max_credentials}
                    onChange={(e) => setLimits({ ...limits, max_credentials: parseInt(e.target.value) || 0 })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                    data-testid="input-max-credentials"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">-1 = unlimited</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Max Certificates</label>
                  <input
                    type="number"
                    value={limits.max_certificates}
                    onChange={(e) => setLimits({ ...limits, max_certificates: parseInt(e.target.value) || 0 })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                    data-testid="input-max-certificates"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">-1 = unlimited</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Max Users</label>
                  <input
                    type="number"
                    value={limits.max_users}
                    onChange={(e) => setLimits({ ...limits, max_users: parseInt(e.target.value) || 0 })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                    data-testid="input-max-users"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">-1 = unlimited</p>
                </div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={limits.file_attachments || false}
                    onChange={(e) => setLimits({ ...limits, file_attachments: e.target.checked })}
                    className="accent-amber-500 w-4 h-4"
                    data-testid="check-file-attachments"
                  />
                  <span className="text-gray-300 text-sm">File Attachments</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={limits.export || false}
                    onChange={(e) => setLimits({ ...limits, export: e.target.checked })}
                    className="accent-amber-500 w-4 h-4"
                    data-testid="check-export"
                  />
                  <span className="text-gray-300 text-sm">Export to Excel</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={limits.audit_logs || false}
                    onChange={(e) => setLimits({ ...limits, audit_logs: e.target.checked })}
                    className="accent-amber-500 w-4 h-4"
                    data-testid="check-audit-logs"
                  />
                  <span className="text-gray-300 text-sm">Audit Logs Access</span>
                </label>
              </div>
              <button
                onClick={handleSaveLimits}
                disabled={savingLimits}
                className="w-full bg-gray-700 hover:bg-gray-600 text-gray-200 py-2 rounded-lg text-sm font-medium transition-colors"
                data-testid="btn-save-plan-limits"
              >
                {savingLimits ? 'Saving...' : 'Save Plan Limits'}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => onSave(org.id, plan, status)}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold py-2.5 rounded-lg transition-colors"
              data-testid="btn-save-subscription"
            >
              Save Plan & Status
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium py-2.5 rounded-lg border border-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditOrgModal({ org, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: org.name || '',
    details: org.details || '',
    primary_contact_name: org.primary_contact_name || '',
    primary_contact_email: org.primary_contact_email || '',
    primary_contact_phone: org.primary_contact_phone || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    onSave(org.id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="edit-org-modal">
      <div className="bg-gray-900 w-full max-w-lg rounded-xl border border-gray-800 shadow-2xl">
        <div className="flex justify-between items-center p-5 border-b border-gray-800">
          <h2 className="font-manrope font-bold text-lg text-white">Edit Organization</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Company Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
              placeholder="e.g., ACME Corporation"
              data-testid="input-org-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Company Details</label>
            <textarea
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none resize-none"
              placeholder="Industry, address, notes..."
              data-testid="input-org-details"
            />
          </div>

          <div className="border-t border-gray-800 pt-4">
            <p className="text-sm font-medium text-amber-400 mb-3">Primary Contact</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Contact Name</label>
                <input
                  type="text"
                  value={formData.primary_contact_name}
                  onChange={(e) => setFormData({ ...formData, primary_contact_name: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
                  placeholder="John Doe"
                  data-testid="input-contact-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={formData.primary_contact_email}
                    onChange={(e) => setFormData({ ...formData, primary_contact_email: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
                    placeholder="john@company.com"
                    data-testid="input-contact-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={formData.primary_contact_phone}
                    onChange={(e) => setFormData({ ...formData, primary_contact_phone: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
                    placeholder="+1 (555) 123-4567"
                    data-testid="input-contact-phone"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold py-2.5 rounded-lg transition-colors"
              data-testid="btn-save-org"
            >
              Save Changes
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium py-2.5 rounded-lg border border-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
